import { useEffect, useRef } from "react";
import { useLocation } from "@remix-run/react";

import { EXCLUDED_PAGE_VIEW_PATH_PREFIXES } from "~/lib/constants";
import { SELF_REPORTED_PAGE_VIEW_PATHS } from "~/lib/page-view-self-report";

function shouldTrackPath(path: string): boolean {
    // AIDEV-NOTE: Pages that log their own view (to attach experiment
    // metadata) are skipped here so we do not write two rows per visit.
    if (SELF_REPORTED_PAGE_VIEW_PATHS.some((p) => path.startsWith(p))) {
        return false;
    }
    return !EXCLUDED_PAGE_VIEW_PATH_PREFIXES.some((prefix) =>
        path.startsWith(prefix),
    );
}

/**
 * Tracks page views on client-side navigations by POSTing to /api/page-view.
 * Must be rendered inside the authenticated app tree (after SupabaseSessionProvider).
 *
 * AIDEV-NOTE: This replaces the server-side trackPageView() in root loader,
 * which only fired on full page loads (SSR), not SPA navigations.
 */
export function PageViewTracker() {
    const location = useLocation();
    const previousPathRef = useRef<string | null>(null);

    useEffect(() => {
        const path = location.pathname;

        // Skip if same path (e.g. hash/search changes) or excluded
        if (path === previousPathRef.current) {
            return;
        }
        if (!shouldTrackPath(path)) {
            // AIDEV-NOTE: Still advance the ref. A skipped page is a real step
            // in the journey, so leaving it stale would make the NEXT view
            // record the page before it as its internal referrer.
            previousPathRef.current = path;
            return;
        }

        // AIDEV-NOTE: For SPA navigations, use the previous internal path as referrer.
        // document.referrer only reflects the external referrer from the initial page load.
        const referrer = previousPathRef.current || document.referrer || null;
        previousPathRef.current = path;

        // Fire-and-forget POST to page view endpoint
        void fetch("/api/page-view", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                path,
                referrer,
            }),
        });
    }, [location.pathname]);

    return null;
}
